import { sparkToast } from '@spark';

/**
 * Column overlap for attaching more than one dataset or benchmark on the same
 * side. Exact string match after unwrapping `{ value, label }` objects.
 * Dropdown order follows the first list.
 */

export const COLUMN_OVERLAP = Object.freeze({
    single: 'single',
    identical: 'identical',
    partial: 'partial',
    none: 'none',
    unknown: 'unknown',
});

export function normalizeColumnName(column) {
    if (column == null) return '';
    if (typeof column === 'object') {
        return String(column.value || column.label || '').trim();
    }
    return String(column).trim();
}

export function normalizeColumnList(columns) {
    const seen = new Set();
    const result = [];
    for (const column of columns || []) {
        const name = normalizeColumnName(column);
        if (!name || seen.has(name)) continue;
        seen.add(name);
        result.push(name);
    }
    return result;
}

/**
 * Raw library columns for overlap checks. Prefer `file_column_names` and skip
 * `resolveLibraryPickDefaults`, which can inject a synthetic `labels` column.
 */
export function columnsFromLibraryOption(option) {
    return normalizeColumnList(option?.file_column_names || []);
}

export function intersectColumnLists(columnLists) {
    const lists = (columnLists || []).map((list) => normalizeColumnList(list));
    if (!lists.length) return [];
    const [first, ...rest] = lists;
    if (!rest.length) return first;
    return first.filter((name) => rest.every((list) => list.includes(name)));
}

function sameColumnSet(a, b) {
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((name) => other.has(name));
}

/**
 * @returns {{ status: string, shared: string[] }}
 * `unknown` when 2+ lists exist but at least one has no column names yet
 * (do not block; validate again after columns load).
 */
export function classifyColumnOverlap(columnLists) {
    const lists = (columnLists || []).map((list) => normalizeColumnList(list));
    if (lists.length <= 1) {
        return { status: COLUMN_OVERLAP.single, shared: lists[0] || [] };
    }
    if (lists.some((list) => list.length === 0)) {
        return { status: COLUMN_OVERLAP.unknown, shared: intersectColumnLists(lists) };
    }
    const shared = intersectColumnLists(lists);
    if (!shared.length) {
        return { status: COLUMN_OVERLAP.none, shared: [] };
    }
    const identical = lists.every((list) => sameColumnSet(list, shared));
    return {
        status: identical ? COLUMN_OVERLAP.identical : COLUMN_OVERLAP.partial,
        shared,
    };
}

/**
 * Keep files in order; drop any whose columns would empty the running
 * intersection. Files still loading columns (`columnsLoading`) are kept.
 * Entries with an empty loaded column list are dropped (cannot share).
 */
export function partitionEntriesByColumnOverlap(entries) {
    const kept = [];
    const rejected = [];
    let running = null;
    for (const entry of entries || []) {
        if (entry?.columnsLoading) {
            kept.push(entry);
            continue;
        }
        const names = normalizeColumnList(entry?.columns);
        if (!names.length) {
            rejected.push(entry);
            continue;
        }
        if (running == null) {
            running = names;
            kept.push(entry);
            continue;
        }
        const nextShared = intersectColumnLists([running, names]);
        if (!nextShared.length) {
            rejected.push(entry);
            continue;
        }
        running = nextShared;
        kept.push(entry);
    }
    return { kept, rejected, shared: running || [] };
}

/**
 * Cross-target ("share the same details") column compatibility between the
 * dataset and benchmark slots. Mismatch only when BOTH sides hold file
 * entries whose columns have fully loaded and the combined intersection is
 * empty. While any file is still loading columns — or a side has no files,
 * or a loaded side reported no column names — the result is `false`, so the
 * share toggle never flips mid-load or on incomplete information.
 */
export function crossTargetColumnsMismatch(datasetEntries, benchmarkEntries) {
    const dataset = Array.isArray(datasetEntries) ? datasetEntries : [];
    const benchmark = Array.isArray(benchmarkEntries) ? benchmarkEntries : [];
    if (!dataset.length || !benchmark.length) return false;
    const all = [...dataset, ...benchmark];
    if (all.some((entry) => entry?.columnsLoading)) return false;
    // `classifyColumnOverlap` reports `unknown` (not `none`) when any file
    // has an empty column list, so those cases stay non-blocking here.
    const overlap = classifyColumnOverlap(all.map((entry) => entry?.columns));
    return overlap.status === COLUMN_OVERLAP.none;
}

/**
 * True when a 2+ file batch has no shared columns and partition would keep
 * only a leftover file. Abort the whole batch instead of opening the upload
 * modal (or details form) with that leftover.
 *
 * A compatible prefix of 2+ files is not aborted — those stay and the
 * incompatible tail is dropped separately.
 */
export function shouldAbortUploadForNoSharedColumns(entries) {
    const files = Array.isArray(entries) ? entries : [];
    if (files.length < 2) return false;
    if (files.some((entry) => entry?.columnsLoading)) return false;
    const overlap = classifyColumnOverlap(files.map((entry) => entry?.columns));
    if (overlap.status !== COLUMN_OVERLAP.none) return false;
    const { kept } = partitionEntriesByColumnOverlap(files);
    return kept.length < 2;
}

export const SHARED_COLUMNS_WARNING =
    'These files do not share all columns. Only shared columns are available.';

export const SHARED_COLUMNS_LIBRARY_ERROR = 'Selected files have no columns in common.';

export const SHARED_COLUMNS_CARD_ERROR =
    'Files have no shared columns. Please upload files with matching columns';

export function sharedColumnsToastContent(rejectedEntries) {
    const names = (rejectedEntries || [])
        .map((entry) => entry?.uploadName || entry?.file?.name || entry?.librarySourceName)
        .filter(Boolean);
    if (names.length === 1) {
        return `"${names[0]}" has no columns in common with the other selected files.`;
    }
    if (names.length > 1) {
        return `${names.length} files have no columns in common with the other selected files.`;
    }
    return 'Selected files have no columns in common.';
}

export function showNoSharedColumnsToast(rejectedEntries) {
    sparkToast.error('No shared columns', {
        content: sharedColumnsToastContent(rejectedEntries),
        detailed: true,
        duration: 5000,
    });
}
